// ==========================================
// Base Types
// ==========================================

import type {
  ActionItemStatus,
  ActivityLogAction,
  ActivityLogEntityType,
  ActivityType,
  ApplicationPlatform,
  ApplicationStatus,
  AppointmentStatus,
  AppointmentType,
  AttachmentEntityType,
  AttachmentFileType,
  BranchStatus,
  BranchType,
  CommentEntityType,
  DefinitionStatus,
  DeliverableStatus,
  DeliverableType,
  DevelopmentStatus,
  DocumentEntityType,
  DocumentShareMode,
  DocumentType,
  EpicStatus,
  ImplementationStatus,
  ImplementationType,
  Language,
  MeetingAttendeeRole,
  MeetingStatus,
  MeetingType,
  PetitionCategory,
  PetitionStatus,
  Priority,
  ProjectStatus,
  ProjectType,
  ProspectStage,
  QuotationStatus,
  ReleaseStatus,
  RequirementType,
  RSVPStatus,
  SalesOrderStatus,
  SprintStatus,
  TaskStatus,
  TaskType,
  Theme,
  TicketStatus,
  TicketType,
  UserRole,
  VersionEntryType,
} from '../core-types/index.js'
export type {
  ActionItemStatus,
  ActivityLogAction,
  ActivityLogEntityType,
  ActivityType,
  ApplicationPlatform,
  ApplicationStatus,
  AppointmentStatus,
  AppointmentType,
  AttachmentEntityType,
  AttachmentFileType,
  BranchStatus,
  BranchType,
  CommentEntityType,
  DefinitionStatus,
  DeliverableStatus,
  DeliverableType,
  DevelopmentStatus,
  DocumentEntityType,
  DocumentShareMode,
  DocumentType,
  EpicStatus,
  ImplementationStatus,
  ImplementationType,
  Language,
  MeetingAttendeeRole,
  MeetingStatus,
  MeetingType,
  PetitionCategory,
  PetitionStatus,
  Priority,
  ProjectStatus,
  ProjectType,
  ProspectStage,
  QuotationStatus,
  ReleaseStatus,
  RequirementType,
  RSVPStatus,
  SalesOrderStatus,
  SprintStatus,
  TaskStatus,
  TaskType,
  Theme,
  TicketStatus,
  TicketType,
  UserRole,
  VersionEntryType,
}

// ==========================================
// Tenant (Multi-tenancy)
// ==========================================

export type TenantStatus = 'active' | 'suspended' | 'trial' | 'cancelled'

export type TenantPlan = 'free' | 'starter' | 'professional' | 'enterprise'

export interface TenantBranding {
  logoUrl?: string
  primaryColor?: string
  companyName: string
}

export interface TenantLimits {
  maxUsers: number
  maxProjects: number
  maxStorageMB: number
}

export interface Tenant {
  id: string
  name: string
  slug: string // URL-friendly identifier, unique
  status: TenantStatus
  plan: TenantPlan
  branding: TenantBranding
  limits: TenantLimits
  ownerId: string // superadmin or tenant_admin who owns this tenant
  domain?: string // Optional custom domain
  features?: string[] // Feature flags
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// User & Organization
// ==========================================

export interface User {
  id: string
  tenantId?: string // Multi-tenancy: tenant this user belongs to
  email: string
  displayName: string
  role: UserRole
  organizationId?: string
  isOrganizationOwner?: boolean
  assignedOrganizationIds?: string[]  // Para operadores: orgs que gestiona
  tenantIds?: string[]  // Multi-tenant (Modelo C): allowed-list de tenants del usuario
  tenantRoles?: Record<string, UserRole>  // Multi-tenant: rol por tenant
  mustChangePassword?: boolean  // Clave temporal: forzar cambio en el primer login
  avatarUrl?: string
  phone?: string
  language: Language
  theme: Theme
  notificationPreferences?: NotificationPreferences
  telegramChatId?: string
  telegramUserId?: string
  telegramLinkedAt?: Date
  telegramBotUsername?: string
  isActive: boolean
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

export interface Organization {
  id: string
  tenantId?: string
  name: string
  email?: string
  phone?: string
  address?: string
  website?: string
  industry?: string
  notes?: string
  ownerId?: string
  memberIds: string[]
  isActive: boolean
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// CRM
// ==========================================

export interface Prospect {
  id: string
  tenantId?: string
  companyName: string
  contactName: string
  email: string
  phone?: string
  industry?: string
  source?: string
  stage: ProspectStage
  estimatedValue?: number
  notes?: string
  assignedTo?: string
  convertedToOrganizationId?: string
  boardOrder?: number
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

export interface Activity {
  id: string
  tenantId?: string
  prospectId?: string
  organizationId?: string
  type: ActivityType
  title: string
  description?: string
  date: Date
  duration?: number
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Petitions (Client Requests)
// ==========================================

export interface PetitionComment {
  id: string
  content: string
  isInternal: boolean // If true, only visible to team
  createdAt: Date
  createdBy: string
  createdByName: string
}

export interface Petition {
  id: string
  tenantId?: string
  organizationId: string
  projectId?: string // Optional, can be general or project-specific
  // Basic info
  title: string
  description: string // Short description
  extendedDescription?: string // Extended description in Markdown
  category: PetitionCategory
  priority: Priority
  status: PetitionStatus
  // Client communication
  comments: PetitionComment[]
  // Estimation (filled during costing)
  estimatedHours?: number
  estimatedCost?: number
  estimatedDelivery?: Date
  // Approval
  approvedAt?: Date
  approvedBy?: string
  rejectedAt?: Date
  rejectedBy?: string
  rejectionReason?: string
  // Conversion
  convertedToRequirementId?: string
  convertedAt?: Date
  convertedBy?: string
  // Attachments
  attachments?: string[]
  // Timestamps
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Pinned Documents (shared across entities)
// ==========================================

export interface PinnedDocument {
  id: string          // unique id for this pin
  documentId: string  // reference to document in 'documents' collection
  name: string        // custom label e.g. "Contexto IA", "Plan de Deploy"
  tag: string         // key/category e.g. "context", "agent", "planning", "architecture"
}

// ==========================================
// Projects
// ==========================================

export interface Project {
  id: string
  tenantId?: string
  name: string
  key: string // Project key for issue prefixes, e.g., "PROJ"
  description?: string
  organizationId: string
  type: ProjectType
  status: ProjectStatus
  operatorIds: string[]
  leadId?: string // Project lead/manager
  startDate?: Date
  endDate?: Date
  estimatedBudget?: number
  technologies?: string[]
  repositoryUrl?: string
  productionUrl?: string
  stagingUrl?: string
  // Counters for generating keys
  epicCounter: number // For PROJ-E1, PROJ-E2...
  requirementCounter: number // For PROJ-1, PROJ-2...
  // Default Definition of Done template
  defaultDefinitionOfDone?: string[]
  // Fixed context documents
  contextDocumentId?: string
  agentDocumentId?: string
  planningDocumentId?: string
  // Pinned documents (flexible)
  pinnedDocuments?: PinnedDocument[]
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

export interface DocumentCopilotSummary {
  content: string // Markdown summary
  generatedAt: Date
  generatedByVersion: number // document version when summary was generated
  isStale: boolean // true when document content changed after summary was generated
}

export interface Document {
  id: string
  tenantId?: string
  entityType: DocumentEntityType
  entityId: string
  title: string
  type: DocumentType
  content: string
  version: number
  isPublic: boolean // If true, visible to clients; if false, internal only
  shareMode: DocumentShareMode // External sharing mode
  shareToken?: string // UUID for share link
  sharedWithEmails?: string[] // Allowed emails for restricted mode
  mfaRequiredEmails?: string[] // Subset of sharedWithEmails that require MFA code
  allowExternalComments?: boolean // Allow guest comments
  reactions?: Record<string, string[]>  // { '👍': ['userId1', 'guest:email@x.com'] }
  viewCount?: number
  copilotSummary?: DocumentCopilotSummary
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

export const DOCUMENT_REACTIONS = ['👍', '👎', '❤️', '🎉', '👀', '🚀'] as const

// Legacy type for backwards compatibility
export interface ProjectDocument {
  id: string
  projectId: string
  title: string
  type: DocumentType
  content: string
  version: number
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Epics
// ==========================================

export interface Epic {
  id: string
  tenantId?: string
  projectId: string
  organizationId: string
  applicationId?: string // Optional - links to specific application
  key: string // e.g., "PROJ-E1"
  title: string
  description?: string
  status: EpicStatus
  priority: Priority
  color?: string // For visual identification in board
  startDate?: Date
  targetDate?: Date
  completedAt?: Date
  assignedTo?: string
  labels?: string[]
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Requirements / User Stories
// ==========================================

// Definition of Done item
export interface DoDAItem {
  id: string
  text: string
  completed: boolean
  completedAt?: Date
  completedBy?: string
}

// Acceptance Criteria item
export interface AcceptanceCriterion {
  id: string
  description: string
  completed: boolean
  completedAt?: Date
  completedBy?: string
}

export interface Requirement {
  id: string
  tenantId?: string
  projectId: string
  organizationId: string
  applicationId?: string // Optional - links to specific application
  epicId?: string // Link to parent Epic
  parentId?: string // For subtasks
  key: string // e.g., "PROJ-123"
  title: string
  description: string
  // User Story format: "As a [user], I want [feature] so that [benefit]"
  userStory?: {
    asA: string // Role/persona
    iWant: string // Feature/action
    soThat: string // Benefit/value
  }
  type: RequirementType
  priority: Priority
  definitionStatus: DefinitionStatus
  developmentStatus: DevelopmentStatus
  // Estimation
  storyPoints?: number // Fibonacci: 1, 2, 3, 5, 8, 13, 21
  estimatedHours?: number
  actualHours?: number
  // Definition of Done
  definitionOfDone: DoDAItem[]
  // Acceptance Criteria
  acceptanceCriteria: AcceptanceCriterion[]
  // Assignment
  assignedTo?: string
  reporterId?: string
  // Dates
  startedAt?: Date
  completedAt?: Date
  dueDate?: Date
  // Relations
  releaseId?: string
  sprintId?: string
  blockedByIds?: string[] // IDs of blocking requirements
  // Approval
  approvedAt?: Date
  approvedBy?: string
  // Notes
  clientNotes?: string
  internalNotes?: string
  // Board ordering
  boardOrder?: number
  // Labels/Tags
  labels?: string[]
  // Attachments
  attachments?: string[]
  // Timestamps
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Releases
// ==========================================

export interface Release {
  id: string
  tenantId?: string
  projectId: string
  version: string
  name: string
  description?: string
  status: ReleaseStatus
  requirementIds: string[]
  plannedDate?: Date
  deployedAt?: Date
  releaseNotes?: string
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Sprints
// ==========================================

export interface Sprint {
  id: string
  tenantId?: string
  name: string
  goal?: string
  status: SprintStatus
  startDate?: Date
  endDate?: Date
  completedAt?: Date
  requirementIds: string[]
  // Metrics (cached on each update)
  totalStoryPoints: number
  completedStoryPoints: number
  totalRequirements: number
  completedRequirements: number
  // Audit
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Applications (within Projects)
// ==========================================

export interface VersionEntry {
  id: string
  version: string
  type: VersionEntryType
  releaseDate: Date
  notes?: string
  createdBy: string
}

export interface Application {
  id: string
  tenantId?: string
  projectId: string
  organizationId: string
  operatorIds: string[]
  name: string
  description?: string
  platform: ApplicationPlatform
  status: ApplicationStatus
  currentVersion?: string
  technologies?: string[]
  repositoryUrl?: string
  productionUrl?: string
  stagingUrl?: string
  versionHistory: VersionEntry[]
  // Fixed context documents
  contextDocumentId?: string
  agentDocumentId?: string
  planningDocumentId?: string
  // Pinned documents (flexible)
  pinnedDocuments?: PinnedDocument[]
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Deliverables (within Projects — extensible)
// ==========================================

export interface DeliverableUrl {
  label: string
  url: string
}

export interface DeliverableCustomField {
  id: string
  key: string
  value: string
}

export interface DeliverableEntry {
  id: string
  title: string
  description?: string
  date: Date
  type?: string
  metadata?: Record<string, string>
  createdAt: Date
  createdBy: string
}

export interface DeliverableBase {
  id: string
  tenantId?: string
  projectId: string
  organizationId: string
  type: DeliverableType
  name: string
  description?: string
  status: DeliverableStatus
  operatorIds: string[]
  tags?: string[]
  urls?: DeliverableUrl[]
  // Rich base fields
  responsiblePerson?: string
  startDate?: Date
  endDate?: Date
  budget?: number
  currency?: string
  location?: string
  priority?: 'critical' | 'high' | 'medium' | 'low'
  notes?: string
  // Dynamic key-value fields
  customFields?: DeliverableCustomField[]
  // Entries/records log
  entries?: DeliverableEntry[]
  // Fixed context documents
  contextDocumentId?: string
  agentDocumentId?: string
  planningDocumentId?: string
  // Pinned documents (flexible)
  pinnedDocuments?: PinnedDocument[]
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

export interface ServiceDeliverable extends DeliverableBase {
  type: 'service'
  serviceType?: 'maintenance' | 'consulting' | 'support' | 'managed' | 'training' | 'other'
  provider?: string
  sla?: string
  contractEnd?: Date
  recurrence?: 'one_time' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'
}

export interface InfrastructureDeliverable extends DeliverableBase {
  type: 'infrastructure'
  infrastructureType?: string
  provider?: string
  environment?: string
  region?: string
  capacity?: string
  monthlyCost?: number
  specifications?: string
}

export interface ResearchDeliverable extends DeliverableBase {
  type: 'research'
  hypothesis?: string
  methodology?: string
  findings?: string
  datasets?: string[]
  publications?: string[]
}

export interface ComponentDeliverable extends DeliverableBase {
  type: 'component'
  componentType?: string
  specifications?: string
  supplier?: string
  quantity?: number
  unitCost?: number
}

export interface HardwareDeliverable extends DeliverableBase {
  type: 'hardware'
  model?: string
  manufacturer?: string
  serialNumber?: string
  warrantyEnd?: Date
  specifications?: string
}

export interface OtherDeliverable extends DeliverableBase {
  type: 'other'
}

export type Deliverable =
  | ServiceDeliverable
  | InfrastructureDeliverable
  | ResearchDeliverable
  | ComponentDeliverable
  | HardwareDeliverable
  | OtherDeliverable

// ==========================================
// Branches (within Applications)
// ==========================================

export interface Branch {
  id: string
  tenantId?: string
  applicationId: string
  projectId: string
  organizationId: string
  name: string
  description?: string
  type: BranchType
  status: BranchStatus
  baseBranch: string
  requirementId?: string
  requirementKey?: string
  documentId?: string
  pullRequestNumber?: number
  pullRequestUrl?: string
  operatorIds: string[]
  version?: string
  notes?: string
  mergedAt?: Date
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Implementations
// ==========================================

export interface ImplementationEnvironment {
  id: string;
  name: string;
  url?: string;
  currentVersion?: string;
  updatedAt: Date;
  status: 'active' | 'maintenance' | 'deprecated';
  notes?: string;
}

export interface ImplementationClientContact {
  name: string;
  role: string;
  email: string;
  phone?: string;
}

export interface Implementation {
  id: string
  tenantId?: string
  projectId: string
  applicationId?: string
  organizationId: string
  
  name: string
  description?: string
  type: ImplementationType
  status: ImplementationStatus
  
  leadId?: string
  operatorIds: string[]
  clientContact?: ImplementationClientContact
  
  environments: ImplementationEnvironment[]
  
  startDate?: Date
  targetGoLiveDate?: Date
  actualGoLiveDate?: Date
  completedAt?: Date
  
  contextDocumentId?: string
  agentDocumentId?: string
  planningDocumentId?: string
  pinnedDocuments?: PinnedDocument[]
  
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Tickets
// ==========================================

export interface Ticket {
  id: string
  tenantId?: string
  organizationId: string
  projectId?: string
  requirementId?: string
  type: TicketType
  title: string
  description: string
  priority: Priority
  status: TicketStatus
  operatorIds: string[]
  convertedToRequirementId?: string
  slaResponseDue: Date
  slaResolutionDue: Date
  respondedAt?: Date
  resolvedAt?: Date
  slaResponseMet?: boolean
  slaResolutionMet?: boolean
  attachments?: string[]
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Comments
// ==========================================

export interface Comment {
  id: string
  tenantId?: string
  entityType: CommentEntityType
  entityId: string
  content: string
  isInternal: boolean
  attachments?: string[]
  guestEmail?: string // Email of external visitor
  guestName?: string // Display name of external visitor
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Attachments
// ==========================================

export interface Attachment {
  id: string
  tenantId?: string
  entityType: AttachmentEntityType
  entityId: string
  fileName: string
  originalName: string
  fileType: AttachmentFileType
  mimeType: string
  fileSize: number // in bytes
  url: string
  storagePath: string
  isPublic: boolean // If true, visible to clients; if false, internal only
  createdAt: Date
  createdBy: string
}

// ==========================================
// Quotations
// ==========================================

export interface Quotation {
  id: string
  tenantId?: string
  organizationId?: string
  prospectId?: string
  projectName: string
  description: string
  projectType: ProjectType
  hasDesigns: boolean
  desiredDeadline?: Date
  estimatedBudgetRange?: string
  additionalNotes?: string
  attachments?: string[]
  status: QuotationStatus
  quotedAmount?: number
  validUntil?: Date
  convertedToProjectId?: string
  convertedToSalesOrderId?: string
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Sales Orders
// ==========================================

export interface SalesOrderItem {
  description: string
  quantity: number
  unitPrice: number
  total: number
}

export interface SalesOrder {
  id: string
  tenantId?: string
  organizationId: string
  quotationId?: string
  projectId?: string
  orderNumber: string
  status: SalesOrderStatus
  items: SalesOrderItem[]
  subtotal: number
  taxRate: number
  taxAmount: number
  discount?: number
  total: number
  notes?: string
  validUntil?: Date
  acceptedAt?: Date
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Appointments
// ==========================================

export interface Appointment {
  id: string
  tenantId?: string
  organizationId?: string
  prospectId?: string
  requestedBy: string
  requestedDateTime: Date
  proposedDateTimes?: Date[]
  confirmedDateTime?: Date
  status: AppointmentStatus
  type: AppointmentType
  title: string
  description?: string
  meetingUrl?: string
  assignedTo?: string
  notes?: string
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Meetings
// ==========================================

export interface MeetingAttendee {
  userId?: string
  email: string
  name: string
  role: MeetingAttendeeRole
  rsvpStatus: RSVPStatus
  attended?: boolean
}

export interface MeetingDecision {
  id: string
  description: string
  decidedBy?: string
  createdAt: Date
}

export interface MeetingActionItem {
  id: string
  description: string
  assigneeId?: string
  assigneeName?: string
  dueDate?: Date
  status: ActionItemStatus
  linkedRequirementId?: string
  createdAt: Date
}

export interface MeetingKeyQuestion {
  id: string
  question: string
  timestamp?: string
  createdAt: Date
  answer?: {
    content: string
    answeredBy?: string
    answeredAt: Date
  }
}

export interface Meeting {
  id: string
  tenantId?: string
  projectId: string
  applicationId?: string
  implementationId?: string
  organizationId: string
  operatorIds: string[]
  title: string
  description: string
  meetingType: MeetingType
  status: MeetingStatus
  scheduledDate: Date
  startTime?: string
  endTime?: string
  duration?: number
  location?: string
  attendees: MeetingAttendee[]
  agenda?: string
  notes?: string
  transcriptDocumentId?: string
  decisions: MeetingDecision[]
  actionItems: MeetingActionItem[]
  keyQuestions: MeetingKeyQuestion[]
  tags?: string[]
  createdBy: string
  updatedBy: string
  createdAt: Date
  updatedAt: Date
}

// ==========================================
// Activity Logs
// ==========================================

export interface ActivityLogChange {
  field: string
  oldValue: unknown
  newValue: unknown
}

export interface ActivityLog {
  id: string
  tenantId?: string
  entityType: ActivityLogEntityType
  entityId: string
  action: ActivityLogAction
  changes?: ActivityLogChange[]
  description: string
  createdAt: Date
  createdBy: string
}

// ==========================================
// SLA Configuration
// ==========================================

export interface SLAConfiguration {
  id: string
  tenantId?: string
  priority: Priority
  responseTimeHours: number
  resolutionTimeHours: number
  isActive: boolean
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Notifications
// ==========================================

export type NotificationType =
  // Creation events
  | 'project_created'
  | 'epic_created'
  | 'requirement_created'
  | 'ticket_created'
  | 'quotation_created'
  | 'sales_order_created'
  | 'petition_created'
  | 'appointment_created'
  | 'release_created'
  // Assignment events
  | 'ticket_assigned'
  | 'requirement_assigned'
  // Status change events
  | 'ticket_status_changed'
  | 'requirement_status_changed'
  // Other events
  | 'comment_added'
  | 'appointment_confirmed'
  | 'quotation_responded'
  | 'meeting_created'
  | 'meeting_status_changed'
  | 'sprint_created'
  | 'sprint_started'
  | 'sprint_completed'
  // Scheduled evaluations
  | 'meeting_reminder'
  | 'appointment_reminder'
  | 'requirement_due_soon'
  | 'requirement_overdue'
  | 'action_item_overdue'
  | 'ticket_sla_at_risk'
  | 'ticket_sla_breached'
  | 'sprint_ending_soon'
  | 'quotation_expiring_soon'
  | 'release_approaching'
  // Deliverables
  | 'deliverable_created'
  | 'deliverable_updated'
  | 'deliverable_deleted'
  // CRM & Leads
  | 'prospect_created'
  | 'prospect_stage_changed'
  | 'lead_created'
  | 'lead_status_changed'

export type NotificationEntityType =
  | 'project'
  | 'epic'
  | 'requirement'
  | 'ticket'
  | 'quotation'
  | 'salesOrder'
  | 'petition'
  | 'appointment'
  | 'release'
  | 'application'
  | 'meeting'
  | 'sprint'
  | 'prospect'
  | 'lead'
  | 'deliverable'
  | 'implementation'

export type NotificationCategory =
  | 'projects'
  | 'tickets'
  | 'requirements'
  | 'epics'
  | 'meetings'
  | 'petitions'
  | 'quotations'
  | 'salesOrders'
  | 'releases'
  | 'applications'
  | 'deliverables'
  | 'appointments'
  | 'comments'
  | 'sprints'
  | 'prospects'
  | 'leads'
  // Scheduled alert categories
  | 'reminders'
  | 'deadlines'
  | 'slaAlerts'
  | 'scheduling'

export type NotificationDeliveryMode = 'off' | 'immediate' | 'daily' | 'weekly'

export interface NotificationPreferences {
  projects: boolean
  tickets: boolean
  requirements: boolean
  epics: boolean
  meetings: boolean
  petitions: boolean
  quotations: boolean
  salesOrders: boolean
  releases: boolean
  applications: boolean
  deliverables: boolean
  appointments: boolean
  comments: boolean
  sprints: boolean
  prospects: boolean
  leads: boolean
  // Scheduled alert categories
  reminders: boolean
  deadlines: boolean
  slaAlerts: boolean
  scheduling: boolean
  // Granular type overrides (in-app)
  typeOverrides?: Partial<Record<NotificationType, boolean>>
  // Email channel
  emailEnabled: boolean
  emailDelivery?: Partial<Record<NotificationCategory, NotificationDeliveryMode>>
}

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  projects: true,
  tickets: true,
  requirements: true,
  epics: true,
  meetings: true,
  petitions: true,
  quotations: true,
  salesOrders: true,
  releases: true,
  applications: true,
  deliverables: true,
  appointments: true,
  comments: true,
  sprints: true,
  prospects: true,
  leads: true,
  reminders: true,
  deadlines: true,
  slaAlerts: true,
  scheduling: true,
  emailEnabled: false,
}

export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  // Creation events
  project_created: 'Proyecto creado',
  epic_created: 'Épica creada',
  requirement_created: 'Requerimiento creado',
  ticket_created: 'Ticket creado',
  quotation_created: 'Cotización creada',
  sales_order_created: 'Orden de venta creada',
  petition_created: 'Petición creada',
  appointment_created: 'Cita creada',
  release_created: 'Release creado',
  // Assignment events
  ticket_assigned: 'Ticket asignado',
  requirement_assigned: 'Requerimiento asignado',
  // Status change events
  ticket_status_changed: 'Cambio de estado de ticket',
  requirement_status_changed: 'Cambio de estado de requerimiento',
  // Other events
  comment_added: 'Comentario agregado',
  appointment_confirmed: 'Cita confirmada',
  quotation_responded: 'Respuesta a cotización',
  meeting_created: 'Reunión creada',
  meeting_status_changed: 'Cambio de estado de reunión',
  sprint_created: 'Sprint creado',
  sprint_started: 'Sprint iniciado',
  sprint_completed: 'Sprint completado',
  // Scheduled evaluations
  meeting_reminder: 'Recordatorio de reunión',
  appointment_reminder: 'Recordatorio de cita',
  requirement_due_soon: 'Requerimiento por vencer',
  requirement_overdue: 'Requerimiento vencido',
  action_item_overdue: 'Acción pendiente vencida',
  ticket_sla_at_risk: 'SLA de ticket en riesgo',
  ticket_sla_breached: 'SLA de ticket incumplido',
  sprint_ending_soon: 'Sprint por cerrar',
  quotation_expiring_soon: 'Cotización por expirar',
  release_approaching: 'Release próximo',
  // Deliverables
  deliverable_created: 'Entregable creado',
  deliverable_updated: 'Entregable actualizado',
  deliverable_deleted: 'Entregable eliminado',
  // CRM & Leads
  prospect_created: 'Prospecto creado',
  prospect_stage_changed: 'Cambio de etapa de prospecto',
  lead_created: 'Lead creado',
  lead_status_changed: 'Cambio de estado de lead',
}

export const NOTIFICATION_TYPE_TO_CATEGORY: Record<NotificationType, NotificationCategory> = {
  project_created: 'projects',
  epic_created: 'epics',
  requirement_created: 'requirements',
  requirement_assigned: 'requirements',
  requirement_status_changed: 'requirements',
  ticket_created: 'tickets',
  ticket_assigned: 'tickets',
  ticket_status_changed: 'tickets',
  quotation_created: 'quotations',
  quotation_responded: 'quotations',
  sales_order_created: 'salesOrders',
  petition_created: 'petitions',
  appointment_created: 'appointments',
  appointment_confirmed: 'appointments',
  release_created: 'releases',
  comment_added: 'comments',
  meeting_created: 'meetings',
  meeting_status_changed: 'meetings',
  sprint_created: 'sprints',
  sprint_started: 'sprints',
  sprint_completed: 'sprints',
  // Scheduled evaluations
  meeting_reminder: 'reminders',
  appointment_reminder: 'reminders',
  requirement_due_soon: 'deadlines',
  requirement_overdue: 'deadlines',
  action_item_overdue: 'deadlines',
  ticket_sla_at_risk: 'slaAlerts',
  ticket_sla_breached: 'slaAlerts',
  sprint_ending_soon: 'scheduling',
  quotation_expiring_soon: 'scheduling',
  release_approaching: 'scheduling',
  // Deliverables
  deliverable_created: 'deliverables',
  deliverable_updated: 'deliverables',
  deliverable_deleted: 'deliverables',
  prospect_created: 'prospects',
  prospect_stage_changed: 'prospects',
  lead_created: 'leads',
  lead_status_changed: 'leads',
}

// ==========================================
// Notification Grouping
// ==========================================

export type NotificationGroup =
  | 'projects'
  | 'backlog'
  | 'meetings'
  | 'support'
  | 'commercial'
  | 'releases'
  | 'crm'

export const NOTIFICATION_CATEGORY_TO_GROUP: Record<NotificationCategory, NotificationGroup> = {
  projects: 'projects',
  epics: 'projects',
  applications: 'projects',
  deliverables: 'projects',
  requirements: 'backlog',
  sprints: 'backlog',
  meetings: 'meetings',
  appointments: 'meetings',
  tickets: 'support',
  petitions: 'support',
  comments: 'support',
  quotations: 'commercial',
  salesOrders: 'commercial',
  releases: 'releases',
  prospects: 'crm',
  leads: 'crm',
  // Scheduled alert categories
  reminders: 'meetings',
  deadlines: 'backlog',
  slaAlerts: 'support',
  scheduling: 'releases',
}

export const NOTIFICATION_GROUP_LABELS: Record<NotificationGroup, string> = {
  projects: 'Proyectos',
  backlog: 'Backlog',
  meetings: 'Reuniones',
  support: 'Soporte',
  commercial: 'Comercial',
  releases: 'Releases',
  crm: 'CRM & Leads',
}

export const NOTIFICATION_GROUP_ORDER: NotificationGroup[] = [
  'projects',
  'backlog',
  'meetings',
  'support',
  'commercial',
  'releases',
  'crm',
]

export interface Notification {
  id: string
  tenantId?: string
  userId: string
  type: NotificationType
  title: string
  message: string
  entityType?: NotificationEntityType
  entityId?: string
  isRead: boolean
  createdAt: Date
  createdBy?: string
  /** Bolsa de datos opcional para notificaciones accionables (p.ej. { action: 'approve_pipeline', pipelineId, phase }). */
  data?: Record<string, unknown>
}

// ==========================================
// AI Chat (Petition Assistant)
// ==========================================

export type AIChatMessageRole = 'user' | 'assistant' | 'system'

export interface AIChatMessage {
  id: string
  role: AIChatMessageRole
  content: string
  createdAt: Date
}

export interface PetitionDraft {
  title: string
  description: string // Short description (1-2 sentences)
  extendedDescription?: string // Extended description in Markdown
  category: PetitionCategory
  priority: Priority
}

export interface AIChatContext {
  projectId?: string
  projectName?: string
  projectDescription?: string
  organizationId?: string
  organizationName?: string
  existingRequirements?: string[]
  // Context document content (admin only, from project's contextDocumentId)
  contextDocumentContent?: string
}

export type AIChatSessionStatus = 'active' | 'completed' | 'expired'

export interface AIChatSession {
  id: string
  tenantId?: string
  organizationId: string
  userId: string
  projectId?: string
  status: AIChatSessionStatus
  messages: AIChatMessage[]
  context?: AIChatContext
  draft?: PetitionDraft
  createdAt: Date
  updatedAt: Date
}

export interface AIUsageStats {
  id: string
  tenantId?: string
  organizationId: string
  month: string // YYYY-MM format
  messageCount: number
  tokenCount: number
  lastUsedAt: Date
}

// ==========================================
// Copilot
// ==========================================

export type CopilotSessionStatus = 'active' | 'completed' | 'expired'

export type CopilotTemplateCategory =
  | 'scope'
  | 'architecture'
  | 'specifications'
  | 'manual'
  | 'status_report'
  | 'meeting_minutes'
  | 'technical_review'
  | 'custom'

export interface CopilotContextItem {
  type: 'document' | 'attachment' | 'application' | 'requirement' | 'meeting'
  id: string
  title: string
  summary?: string
  content?: string
  entityType?: string
  selected: boolean
}

export interface CopilotProjectContext {
  projectId: string
  projectName: string
  projectDescription: string
  projectStatus: string
  organizationId: string
  organizationName: string
  contextDocumentContent?: string
  items: CopilotContextItem[]
}

export interface CopilotContext {
  project: CopilotProjectContext
  selectedItemIds: string[]
  templateId?: string
  customInstructions?: string
}

export interface CopilotTemplate {
  id: string
  category: CopilotTemplateCategory
  title: string
  description: string
  promptTemplate: string
  icon: string
  requiredContextTypes?: string[]
}

export interface GeneratedDocument {
  title: string
  content: string
  documentType: DocumentType
  suggestedTags?: string[]
}

export interface CopilotMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  createdAt: Date
  metadata?: {
    templateUsed?: string
    contextItemCount?: number
    tokensUsed?: number
  }
}

export interface CopilotSession {
  id: string
  tenantId?: string
  userId: string
  organizationId: string
  projectId: string
  status: CopilotSessionStatus
  messages: CopilotMessage[]
  context?: CopilotContext
  generatedDocument?: GeneratedDocument
  createdAt: Date
  updatedAt: Date
}

// ==========================================
// Utility Types
// ==========================================

export type WithId<T> = T & { id: string }

export type CreateInput<T> = Omit<T, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>

export type UpdateInput<T> = Partial<Omit<T, 'id' | 'createdAt' | 'createdBy'>>

// ==========================================
// Help Center
// ==========================================

export * from './help'

// ==========================================
// Leads
// ==========================================

export * from './lead'

// ==========================================
// Blog
// ==========================================

export * from './blog'

// ==========================================
// Tasks
// ==========================================

export const TASK_LABEL_COLORS = [
  { value: 'green', bg: 'bg-green-500', light: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  { value: 'yellow', bg: 'bg-yellow-500', light: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' },
  { value: 'orange', bg: 'bg-orange-500', light: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
  { value: 'red', bg: 'bg-red-500', light: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
  { value: 'purple', bg: 'bg-purple-500', light: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
  { value: 'blue', bg: 'bg-blue-500', light: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  { value: 'sky', bg: 'bg-sky-500', light: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200' },
  { value: 'pink', bg: 'bg-pink-500', light: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200' },
  { value: 'gray', bg: 'bg-gray-500', light: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' },
] as const

export interface TaskLabel {
  id: string
  name: string
  color: string // value from TASK_LABEL_COLORS
}

export interface TaskChecklistItem {
  id: string
  text: string
  completed: boolean
  completedAt?: Date | null
  completedBy?: string | null
}

export interface TaskBoardList {
  id: string
  name: string
  color?: string
  position: number
}

export interface TaskBoard {
  id: string
  tenantId?: string
  name: string
  description?: string
  color?: string
  lists: TaskBoardList[]
  members: string[]
  archived: boolean
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

export interface TaskLink {
  id: string
  url: string
  title?: string
}

export interface Task {
  id: string
  tenantId?: string
  projectId?: string
  parentTaskId?: string
  boardId?: string
  listId?: string
  taskNumber?: number
  title: string
  description: string
  type: TaskType
  priority: Priority
  status: TaskStatus
  assignedTo: string[]
  reporterId: string
  checklist: TaskChecklistItem[]
  links: TaskLink[]
  progress: number
  labels: string[] // backwards compat text labels
  labelIds: string[] // references to TaskLabel IDs
  dueDate?: Date
  startedAt?: Date
  completedAt?: Date
  estimatedHours?: number
  archived: boolean
  boardOrder: number
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Integrations
// ==========================================

export type IntegrationProvider = 'google' | 'trello'

export type IntegrationStatus = 'active' | 'needsReauth' | 'revoked' | 'error'

export interface UserIntegration {
  id: string
  userId: string
  provider: IntegrationProvider
  label?: string
  providerAccountId: string
  providerAccountEmail: string
  providerDisplayName?: string
  providerPhotoUrl?: string
  status: IntegrationStatus
  isDefault: boolean
  enabledServices: string[]
  scopes: string[]
  accessToken?: string
  refreshToken?: string
  tokenExpiryDate?: Date | null
  lastSyncAt?: Date | null
  lastUsedAt?: Date | null
  lastErrorCode?: string | null
  lastErrorMessage?: string | null
  metadata?: Record<string, unknown>
  createdAt: Date
  updatedAt: Date
  createdBy: string
  updatedBy: string
}

// ==========================================
// Timeline Events
// ==========================================

export type TimelineEntityType = 'project' | 'application' | 'implementation' | 'epic' | 'requirement' | 'ticket'
export type TimelineEventType = 'milestone' | 'meeting' | 'release' | 'note' | 'update' | 'incident'
export type TimelineEventStatus = 'planned' | 'completed' | 'cancelled'

export interface TimelineEvent {
  id: string
  tenantId?: string
  organizationId: string
  
  // Polymorphic association
  entityType: TimelineEntityType
  entityId: string
  
  // Event details
  title: string
  description?: string
  type: TimelineEventType
  status: TimelineEventStatus
  eventDate: Date
  
  // Traceability
  createdBy: string 
  createdAt: Date
  updatedAt: Date
}
